import React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import type { Opportunity, UserRole } from '../types';
import SparklesIcon from './icons/SparklesIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import OpportunityCard from './PostCard';
import FilterBar from './FilterBar';
import { ROLE_TYPE_MAP, isPast } from '../utils/recommendationUtils';

const normalize = (val: string) => (val || '').toLowerCase().split(',')[0].trim();

const RecommendedPage: React.FC = () => {
    const { currentUser, managedProfile } = useAuth();
    const { opportunities, filters, handleManualFilterChange, hasMore, isLoadingMore, loadMoreOpportunities } = useOpportunities();
    const [showPast, setShowPast] = React.useState(false);

    // Eagerly load a second batch on mount so that older-but-future events are visible
    React.useEffect(() => {
        if (hasMore && !isLoadingMore) loadMoreOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scoredOpportunities = React.useMemo(() => {
        if (!currentUser) return [];

        const activeTier = managedProfile?.subscriptionTier || currentUser?.subscriptionTier;
        const isFan = activeTier === 'fan_basic';
        const fanAllowedTypes: Opportunity['type'][] = ['Gig', 'Event', 'Jam Session', 'Live Show', 'Stage Play', 'Open Mic', 'DJ set', 'Stand Up Comedy', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'];

        const userInstruments = (currentUser.instruments || []).map(i => i.toLowerCase());
        const userGenres = (currentUser.genres || []).map(g => g.toLowerCase());
        const userRoles: UserRole[] = (currentUser.roles || []) as UserRole[];
        const relevantTypes = new Set(userRoles.flatMap(role => ROLE_TYPE_MAP[role] || []));
        const userHomeCity = normalize(currentUser.city || '');

        return opportunities
            .map((opp: Opportunity) => {
                if (opp.author.handle === currentUser.handle || opp.status === 'closed') {
                    return { opp, score: -1 };
                }
                if (isFan && !fanAllowedTypes.includes(opp.type)) {
                    return { opp, score: -1 };
                }

                const oppCity = normalize(opp.location);
                let score = 1;

                // Role match → opportunity type
                if (relevantTypes.has(opp.type)) score += 8;

                // Home city match
                if (userHomeCity && oppCity === userHomeCity) score += 10;

                // Travel plan overlap
                if (currentUser.travelPlans?.length) {
                    for (const plan of currentUser.travelPlans) {
                        if (!plan.city) continue;
                        if (normalize(plan.city) !== oppCity) continue;
                        const oppStart = new Date(`${opp.startDate || '1970-01-01'}T00:00:00`);
                        const oppEnd = opp.endDate ? new Date(`${opp.endDate}T00:00:00`) : oppStart;
                        const planStart = new Date(`${plan.startDate}T00:00:00`);
                        const planEnd = new Date(`${plan.endDate}T00:00:00`);
                        if (oppStart <= planEnd && oppEnd >= planStart) { score += 15; break; }
                    }
                }

                // Instruments & genres
                const oppInstruments = (opp.instrument || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
                if (oppInstruments.some(i => userInstruments.includes(i))) score += 10;
                const oppGenres = (opp.genre || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
                if (oppGenres.some(g => userGenres.includes(g))) score += 5;

                if (opp.salary) score += 3;
                if (['Jam Session', 'Open Mic', 'Event', 'DJ set', 'Stand Up Comedy'].includes(opp.type)) score += 2;

                return { opp, score };
            })
            .filter(item => item.score >= 0)
            .sort((a, b) => b.score - a.score)
            .map(item => item.opp);
    }, [currentUser, managedProfile, opportunities]);

    const filteredRecommended = React.useMemo(() => {
        return scoredOpportunities.filter((opp: Opportunity) => {
            if (filters.type && opp.type !== filters.type) return false;
            if (filters.roleNeeded && opp.roleNeeded !== filters.roleNeeded) return false;
            if (filters.city && !opp.location.toLowerCase().includes(filters.city.toLowerCase())) return false;
            if (filters.region && (!opp.region || !opp.region.toLowerCase().includes(filters.region.toLowerCase()))) return false;
            if (filters.country && (!opp.country || !opp.country.toLowerCase().includes(filters.country.toLowerCase()))) return false;
            if (filters.instrument) {
                if (!opp.instrument) return false;
                if (!opp.instrument.toLowerCase().includes(filters.instrument.toLowerCase())) return false;
            }
            if (filters.genre) {
                if (!opp.genre) return false;
                if (!opp.genre.toLowerCase().includes(filters.genre.toLowerCase())) return false;
            }
            if (filters.paidOnly && !opp.salary) return false;
            return true;
        });
    }, [scoredOpportunities, filters]);

    const activeOpps = React.useMemo(() => filteredRecommended.filter(o => !isPast(o)), [filteredRecommended]);
    const pastOpps   = React.useMemo(() => filteredRecommended.filter(o =>  isPast(o)), [filteredRecommended]);

    return (
        <div className="space-y-6 font-urbanist animate-in fade-in duration-500">
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <div className="flex items-center space-x-3">
                    <SparklesIcon className="h-7 w-7 text-brand-orange" />
                    <h2 className="text-3xl font-black text-white uppercase tracking-tight">For You</h2>
                </div>
                <p className="mt-2 text-brand-muted">
                    Opportunities matched by your roles, home city, travel plans, and skills. Past events are hidden below.
                </p>
            </div>

            <FilterBar filters={filters} onFilterChange={handleManualFilterChange} />

            {activeOpps.length > 0 ? (
                <div className="space-y-4">
                    {activeOpps.map(opportunity => (
                        <OpportunityCard key={opportunity.id} opportunity={opportunity} />
                    ))}
                </div>
            ) : (
                <div className="bg-brand-surface rounded-lg p-20 text-center border-2 border-dashed border-gray-800">
                    <SparklesIcon className="h-16 w-16 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2 tracking-tight uppercase">No results found</h3>
                    <p className="text-brand-muted max-w-sm mx-auto">Try adjusting your filters or update your profile to find more tailored matches.</p>
                </div>
            )}

            {pastOpps.length > 0 && (
                <div className="bg-brand-surface rounded-lg border border-gray-800">
                    <button
                        onClick={() => setShowPast(v => !v)}
                        className="w-full flex items-center justify-between p-5 text-left group"
                    >
                        <div>
                            <span className="font-bold text-brand-muted tracking-widest text-xs uppercase">Past Events</span>
                            <span className="ml-2 text-[10px] bg-gray-800 text-gray-500 px-2 py-0.5 rounded-full border border-gray-700">{pastOpps.length}</span>
                        </div>
                        <ChevronDownIcon className={`h-4 w-4 text-brand-muted transition-transform duration-200 ${showPast ? 'rotate-180' : ''}`} />
                    </button>
                    {showPast && (
                        <div className="px-5 pb-5 space-y-4 border-t border-gray-800 pt-4">
                            {pastOpps.map(opportunity => (
                                <OpportunityCard key={opportunity.id} opportunity={opportunity} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Load more */}
            {hasMore && (
                <div className="text-center pt-2">
                    <button
                        onClick={loadMoreOpportunities}
                        disabled={isLoadingMore}
                        className="px-8 py-3 rounded-full border border-gray-700 text-sm font-bold text-brand-muted hover:border-brand-orange hover:text-brand-orange transition-colors disabled:opacity-50"
                    >
                        {isLoadingMore ? 'Loading...' : 'Load more opportunities'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default RecommendedPage;
